import React, { useState, useEffect } from 'react';
import { ConstructorEventUpdateSQLStatus } from '../../../../Event/event';

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    background: '#ffffffdd',
    borderRadius: '15px',
    padding: '25px 20px 35px 20px',
    boxShadow: '0 8px 20px rgba(30, 70, 110, 0.2)',
    fontFamily: "'Comic Neue', cursive, Arial, sans-serif",
  },
  heading: {
    textAlign: 'center',
    fontWeight: 700,
    color: '#1f4e79',
    marginBottom: '8px',
  },
  subheading: {
    textAlign: 'center',
    marginTop: 0,
    marginBottom: '30px',
    fontWeight: 600,
    color: '#3a6ea5',
    fontSize: '1.1rem',
  },
  label: {
    display: 'block',
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: '6px',
    color: '#2a557f',
  },
  input: {
    width: '100%',
    fontSize: '1rem',
    padding: '8px 10px',
    border: '2px solid #aad2ff',
    borderRadius: '10px',
    outlineOffset: '2px',
    marginBottom: '15px',
  },
  button: {
    background: '#4a90e2',
    color: 'white',
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: '10px 25px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    marginRight: '12px',
    boxShadow: '0 5px 15px rgba(74, 144, 226, 0.4)',
  },
  disabledButton: {
    background: '#a0bde8',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  responsesList: {
    marginTop: '10px',
    fontSize: '1rem',
    color: '#1e3a5f',
    maxHeight: '120px',
    overflowY: 'auto',
    border: '1px solid #aad2ff',
    padding: '8px 12px',
    borderRadius: '10px',
    background: '#f5faff',
  },
  section: {
    marginTop: '25px',
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#1f4e79',
    marginBottom: '12px',
    borderBottom: '2px solid #4a90e2',
    paddingBottom: '6px',
  },
  tallyChart: {
    fontFamily: 'monospace',
    fontSize: '1.2rem',
    whiteSpace: 'pre',
    background: '#dbeeff',
    border: '1px solid #8ab2f7',
    padding: '12px 15px',
    borderRadius: '10px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '12px',
  },
  th: {
    border: '1px solid #4a90e2',
    padding: '8px 12px',
    textAlign: 'center',
    fontWeight: 600,
    background: '#4a90e2',
    color: 'white',
  },
  td: {
    border: '1px solid #4a90e2',
    padding: '8px 12px',
    textAlign: 'center',
    fontWeight: 600,
    background: '#ecf3ff',
  },
};

const SurveyComponent = () => {
  const [topic, setTopic] = useState('');
  const [response, setResponse] = useState('');
  const [responses, setResponses] = useState([]);

  useEffect(() => {
    document.getElementById('topicInput')?.focus();
  }, []);

  const handleAdd = () => {
    if (response.trim() === '') return;
    setResponses([...responses, response.trim()]);
    setResponse('');
  
    const responsedata = ConstructorEventUpdateSQLStatus();
    console.log("Event completed", responsedata);
  };

  const handleClear = () => {
    setTopic('');
    setResponse('');
    setResponses([]);
  };

  const createTallyMarks = (count) => {
    let tally = '';
    for (let i = 1; i <= count; i++) {
      tally += i % 5 === 0 ? '〱 ' : '｜ ';
    }
    return tally.trim();
  };

  const freqMap = {};
  responses.forEach((r) => {
    const key = r.toLowerCase();
    if (!freqMap[key]) {
      freqMap[key] = { label: r, count: 0 };
    }
    freqMap[key].count++;
  });

  const sortedKeys = Object.keys(freqMap).sort();

  return (
    <div style={styles.container} role="main" aria-label="Create your own survey activity">
      <h3 style={styles.heading}>📊 Create Your Own Survey</h3>
      <p style={styles.subheading}>
        Choose a topic and collect responses to see your data in tally chart and frequency table!
      </p>

      <label htmlFor="topicInput" style={styles.label}>1. Enter your survey topic:</label>
      <input
        id="topicInput"
        style={styles.input}
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="e.g. Favorite Fruit"
      />

      <label htmlFor="responseInput" style={styles.label}>2. Add a response:</label>
      <input
        id="responseInput"
        style={styles.input}
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && response.trim() && handleAdd()}
        placeholder="Type a response and press Add"

      />
      <button
        style={{
          ...styles.button,
          ...(response.trim() === '' ? styles.disabledButton : {})
        }}
        disabled={response.trim() === ''}
        onClick={handleAdd}
      >
        Add Response
      </button>
      <button style={styles.button} onClick={handleClear}>Clear All</button>

      <div
        style={styles.responsesList}
        aria-live="polite"
        aria-atomic="true"
        aria-label="List of added responses"
      >
        {responses.length === 0 ? 'No responses added yet.' : responses.join(', ')}
      </div>

      {responses.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Tally Chart</h2>
          <pre style={styles.tallyChart} aria-label="Tally chart">
            {sortedKeys.map((key) => {
              const { label, count } = freqMap[key];
              return `${label.padEnd(15)} : ${createTallyMarks(count)}\n`;
            })}
          </pre>

          <h2 style={styles.sectionTitle}>Frequency Table</h2>
          <table style={styles.table} aria-label="Frequency table">
            <thead>
              <tr>
                <th style={styles.th}>Response</th>
                <th style={styles.th}>Count</th>
              </tr>
            </thead>
            <tbody>
              {sortedKeys.map((key) => (
                <tr key={key}>
                  <td style={styles.td}>{freqMap[key].label}</td>
                  <td style={styles.td}>{freqMap[key].count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SurveyComponent;
